import React from 'react'
import { observer } from 'mobx-react-lite'

import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'

import {
  ANALYTICS_EVENTS,
  Drawer,
  EmptyState,
  Loader,
  useAccountStore,
  useEvent,
  useGlobalCopyStore,
  useGlobalUiStore,
  useGoalStore,
  useScreenSize,
  useWidgetLoadTimer,
  WidgetContainer,
} from '../common'
import { TrackType, WidgetProps } from '../core'

import { TabTitle } from './components/TabTitle'
import { GoalList } from './components/GoalList'

import { AddGoal } from './components/AddGoal'
import { GoalDetailsDrawer } from './components/GoalDetailsDrawer'
import { formatCurrency } from '../core/utils/NumberFormatting'
import { buildDynamicCopy } from '../core/utils/Localization'

export const GoalsWidget = observer(({ onBackClick, onMenuClick, sx }: WidgetProps) => {
  const { onEvent } = useEvent()
  const { isMobile } = useScreenSize()
  const { isAccountDataLoaded, loadAccountData } = useAccountStore()
  const { isInitialized } = useGlobalUiStore()
  const { goals: copy, common: commonCopy } = useGlobalCopyStore()
  const {
    alert,
    allGoals,
    goals,
    isAllGoalDataLoaded,
    loadGoalData,
    monthlyCashFlowProfile,
    setAlert,
    retirmentGoals,
  } = useGoalStore()

  const [selectedTab, setSelectedTab] = React.useState(0)
  const [isAddOpen, setIsAddOpen] = React.useState(false)

  const savingTotal = monthlyCashFlowProfile?.amount_allocated_for_savings_goals || 0
  const debtTotal = monthlyCashFlowProfile?.amount_allocated_for_debt_goals || 0
  const retirementTotal = monthlyCashFlowProfile?.amount_allocated_for_retirement_goals || 0

  useWidgetLoadTimer({
    widgetName: 'GoalsWidget',
    isLoaded: isAllGoalDataLoaded,
  })

  React.useEffect(() => {
    if (!isAccountDataLoaded) {
      loadAccountData().finally()
    }
    onEvent(ANALYTICS_EVENTS.GOALS_VIEW)
  }, [])

  React.useEffect(() => {
    if (isInitialized) {
      loadGoalData().finally()
    }
  }, [isInitialized])

  const handleAccountFilterClick = () => {
    onEvent(ANALYTICS_EVENTS.GOALS_CLICK_FILTER)
  }

  const handleAddClick = () => {
    setIsAddOpen(true)
    onEvent(ANALYTICS_EVENTS.GOALS_CLICK_ADD)
  }

  const handleDetailsClose = (trackType: TrackType) => {
    setIsAddOpen(false)
    if (trackType === TrackType.SAVINGS_TRACK) {
      setSelectedTab(0)
    } else if (trackType === TrackType.DEBT_TRACK) {
      setSelectedTab(1)
    } else if (trackType === TrackType.RETIREMENT_TRACK) {
      setSelectedTab(2)
    }
  }

  const isEmptyGoalsState = React.useMemo(
    () => isAllGoalDataLoaded && allGoals.length === 0,
    [goals, isAllGoalDataLoaded, retirmentGoals],
  )

  if (!isInitialized || !isAllGoalDataLoaded) {
    return <Loader />
  }

  const savingsTabSubtitle = buildDynamicCopy(
    isMobile ? copy.tab_subtitle_mobile : copy.tab_subtitle,
    formatCurrency(savingTotal, '0,0'),
  ) as string

  const debtTabSubtitle = buildDynamicCopy(
    isMobile ? copy.tab_subtitle_mobile : copy.tab_subtitle,
    formatCurrency(debtTotal, '0,0'),
  ) as string

  const retireTabSubtitle = buildDynamicCopy(
    isMobile ? copy.tab_subtitle_mobile : copy.tab_subtitle,
    formatCurrency(retirementTotal, '0,0'),
  ) as string

  return (
    <WidgetContainer
      actions={[
        {
          iconName: 'add',
          label: copy.add_title,
          onClick: handleAddClick,
        },
      ]}
      onAccountsFilterClick={handleAccountFilterClick}
      onBackClick={onBackClick}
      onMenuClick={onMenuClick}
      sx={sx}
      title={copy.title}
    >
      <Stack mx={isMobile ? 0 : 48} my={24}>
        <Tabs
          onChange={(_, newValue) => setSelectedTab(newValue)}
          sx={{ bgcolor: 'background.paper' }}
          textColor="primary"
          value={!isEmptyGoalsState ? selectedTab : false}
          variant="fullWidth"
        >
          <Tab
            disabled={isEmptyGoalsState}
            label={<TabTitle subtitle={savingsTabSubtitle} title={copy.tab_saving_title} />}
          />
          <Tab
            disabled={isEmptyGoalsState}
            label={<TabTitle subtitle={debtTabSubtitle} title={copy.tab_debt_title} />}
          />
          <Tab
            disabled={isEmptyGoalsState}
            label={<TabTitle subtitle={retireTabSubtitle} title={copy.tab_retirement_title} />}
          />
        </Tabs>
        {isEmptyGoalsState ? (
          <EmptyState
            header={copy.create_goals_header}
            icon="trophy"
            onClick={handleAddClick}
            primaryButton={copy.add_goals_button}
            subText={copy.empty_state_subheader}
            sx={{
              width: { sm: 432 },
              '.buttons-footer': {
                width: 139,
              },
            }}
          />
        ) : (
          <>
            {selectedTab === 0 && (
              <GoalList onAddGoals={handleAddClick} trackType={TrackType.SAVINGS_TRACK} />
            )}
            {selectedTab === 1 && (
              <GoalList onAddGoals={handleAddClick} trackType={TrackType.DEBT_TRACK} />
            )}
            {selectedTab === 2 && (
              <GoalList onAddGoals={handleAddClick} trackType={TrackType.RETIREMENT_TRACK} />
            )}
          </>
        )}
      </Stack>

      <GoalDetailsDrawer onClose={handleDetailsClose} />

      <Drawer
        ariaLabelClose={commonCopy.close_aria}
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title={copy.add_goal_title}
      >
        <AddGoal onClose={handleDetailsClose} />
      </Drawer>

      <Snackbar onClose={() => setAlert('')} open={Boolean(alert)}>
        <Alert onClose={() => setAlert('')} severity="success" variant="filled">
          {alert}
        </Alert>
      </Snackbar>
    </WidgetContainer>
  )
})
